import { usePoll as useInertiaPoll } from '@inertiajs/react';
import type { SdkPollControls, SdkUsePollOptions } from '@Pages/pageRuntime/sdk/types';
import { useState } from 'react';

import { useClampedPollInterval } from './pollInterval';

/**
 * Only the page's own data is worth returning on a tick. The other `ShowPage` props —
 * the resource catalog, shared components, navigation — are SDK internals that a poll
 * cannot change, so there is no point serializing them again on every tick.
 *
 * This trims the response, not the server's work: `RenderPage` builds every prop eagerly
 * before Inertia reads the partial-reload header, so a tick still costs a full page
 * render. That is why the docs put a floor under the interval.
 */
const PAGE_DATA_RELOAD_FIELDS = ['pageData'];

/**
 * Refresh the page's route bindings and inputs on an interval by partially reloading
 * `pageData`. The only refresh mechanism available to an anonymous visitor on a public
 * page, because the record API is authenticated.
 *
 * Exposed via `Sdk.hooks.usePoll(interval, options)`.
 */
export function usePoll(interval: number, options?: SdkUsePollOptions): SdkPollControls {
  const clamp = useClampedPollInterval();
  // An unchanged reload leaves every prop equal, so without this state a tick that
  // found nothing new would not re-render the page at all.
  const [lastPolledAt, setLastPolledAt] = useState<Date | null>(null);
  const recordPoll = () => setLastPolledAt(new Date());

  const { start, stop } = useInertiaPoll(
    clamp(interval),
    // Inertia calls onError after applying a reload that preserves validation errors.
    { only: PAGE_DATA_RELOAD_FIELDS, onSuccess: recordPoll, onError: recordPoll },
    { autoStart: options?.autoStart, keepAlive: options?.keepAlive }
  );

  return { start, stop, lastPolledAt };
}
